import React, { useState, useEffect } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, RotateCcw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Switch } from "../../ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../ui/select";
import BookingItemsEditor, {
  validateAllItems as validateBookingItems,
} from "./BookingItemsEditor";
import { SUPPORTED_CURRENCIES } from "../../../lib/currencies";

/**
 * Stage A — Centrale document-edit dialog.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (open) => void
 *   docType: 'packing_list' | 'booking_form' | 'generated_invoice' | 'sales_invoice'
 *   doc: huidige document data
 *   onSaved: (updated) => void
 *
 * Logica wordt config-gedreven via documentConfig in deze file — geen
 * dubbele forms per type. Lijnen optioneel; alleen types met line-items
 * krijgen de Regels-tab.
 */

const documentConfig = {
  packing_list: {
    label: "Paklijst",
    apiPath: (id) => `/admin/packing-lists/${id}`,
    regeneratePath: (id) => `/admin/packing-lists/${id}/regenerate-pdf`,
    linesKey: "items",
    lineLabel: "Regel",
    sections: [
      {
        id: "verzender",
        title: "Verzender",
        fields: [
          { key: "first_name", label: "Voornaam" },
          { key: "last_name", label: "Achternaam" },
          { key: "company_name", label: "Bedrijf" },
          { key: "email", label: "E-mail" },
          { key: "phone", label: "Telefoon" },
          { key: "sender_address", label: "Adres" },
          { key: "sender_postcode", label: "Postcode" },
          { key: "sender_city", label: "Plaats" },
          { key: "sender_country", label: "Land" },
        ],
      },
      {
        id: "ontvanger",
        title: "Ontvanger",
        fields: [
          { key: "recipient_name", label: "Naam" },
          { key: "recipient_company", label: "Bedrijf" },
          { key: "recipient_email", label: "E-mail" },
          { key: "recipient_phone", label: "Telefoon" },
          { key: "recipient_address", label: "Adres" },
          { key: "recipient_city", label: "Plaats" },
          { key: "recipient_country", label: "Land" },
          { key: "recipient_instructions", label: "Instructies", textarea: true },
        ],
      },
      {
        id: "zending",
        title: "Zending",
        fields: [
          { key: "destination", label: "Bestemming" },
          { key: "method", label: "Methode" },
          { key: "w_number", label: "W-nummer" },
          { key: "note", label: "Notitie", textarea: true },
        ],
      },
    ],
    lineFields: [
      { key: "box_number", label: "Doos #", type: "text", width: "w-20" },
      { key: "description", label: "Omschrijving", type: "text", width: "flex-1" },
      { key: "quantity", label: "Aantal", type: "number", min: 1, width: "w-20" },
      { key: "value", label: "Waarde €", type: "number", step: "0.01", min: 0, width: "w-24" },
      { key: "condition", label: "Staat", type: "text", width: "w-24" },
    ],
    emptyLine: { box_number: "", description: "", quantity: 1, value: 0, condition: "" },
    helpText: "Bewerk verzender, ontvanger en regels. Box-nummers en aantallen worden zonder verlies bewaard.",
  },

  booking_form: {
    label: "Boekingsformulier",
    apiPath: (id) => `/admin/booking-forms/${id}`,
    regeneratePath: (id) => `/admin/booking-forms/${id}/regenerate-pdf`,
    linesKey: null,
    sections: [
      {
        id: "verzender",
        title: "Verzender",
        fields: [
          { key: "first_name", label: "Voornaam" },
          { key: "last_name", label: "Achternaam" },
          { key: "company_name", label: "Bedrijf" },
          { key: "email", label: "E-mail" },
          { key: "phone", label: "Telefoon" },
          { key: "whatsapp", label: "WhatsApp" },
          { key: "sender_address", label: "Adres" },
          { key: "sender_postcode", label: "Postcode" },
          { key: "sender_city", label: "Plaats" },
          { key: "sender_country", label: "Land" },
        ],
      },
      {
        id: "ontvanger",
        title: "Ontvanger",
        fields: [
          { key: "recipient_name", label: "Naam" },
          { key: "recipient_company", label: "Bedrijf" },
          { key: "recipient_email", label: "E-mail" },
          { key: "recipient_phone", label: "Telefoon" },
          { key: "recipient_address", label: "Adres" },
          { key: "recipient_city", label: "Plaats" },
          { key: "recipient_country", label: "Land" },
        ],
      },
      {
        id: "zending",
        title: "Zending",
        fields: [
          { key: "shipment_type", label: "Type zending" },
          { key: "destination", label: "Bestemming" },
          { key: "method", label: "Methode" },
          { key: "boxes", label: "Aantal dozen", type: "number" },
          { key: "pallets", label: "Aantal pallets", type: "number" },
          { key: "estimated_weight", label: "Geschat gewicht (kg)", type: "number", step: "0.1" },
          { key: "estimated_cbm", label: "Geschat CBM", type: "number", step: "0.01" },
          { key: "goods_description", label: "Omschrijving goederen", textarea: true },
          { key: "goods_value", label: "Goederenwaarde €", type: "number", step: "0.01" },
        ],
      },
    ],
    helpText: "Bewerk verzender, ontvanger en zendingsgegevens. Het documentnummer blijft hetzelfde.",
  },

  generated_invoice: {
    label: "Waardefactuur / waardeverklaring",
    apiPath: (id) => `/admin/invoices/${id}`,
    regeneratePath: (id) => `/admin/invoices/${id}/regenerate-pdf`,
    linesKey: "line_items",
    lineLabel: "Regel",
    nested: { sender: "sender", receiver: "receiver", shipment: "shipment" },
    sections: [
      {
        id: "verzender",
        title: "Verzender",
        nested: "sender",
        fields: [
          { key: "name", label: "Naam" },
          { key: "company", label: "Bedrijf" },
          { key: "address", label: "Adres" },
          { key: "postcode", label: "Postcode" },
          { key: "city", label: "Plaats" },
          { key: "country", label: "Land" },
          { key: "email", label: "E-mail" },
          { key: "phone", label: "Telefoon" },
        ],
      },
      {
        id: "ontvanger",
        title: "Ontvanger",
        nested: "receiver",
        fields: [
          { key: "name", label: "Naam" },
          { key: "company", label: "Bedrijf" },
          { key: "address", label: "Adres" },
          { key: "postcode", label: "Postcode" },
          { key: "city", label: "Plaats" },
          { key: "country", label: "Land" },
          { key: "email", label: "E-mail" },
          { key: "phone", label: "Telefoon" },
        ],
      },
      {
        id: "zending",
        title: "Zending",
        nested: "shipment",
        fields: [
          { key: "destination", label: "Bestemming" },
          { key: "w_number", label: "W-nummer" },
          { key: "description", label: "Omschrijving", textarea: true },
          { key: "boxes", label: "Aantal dozen", type: "number" },
          { key: "remarks", label: "Opmerkingen", textarea: true },
        ],
      },
    ],
    lineFields: [
      { key: "description", label: "Omschrijving", type: "text", width: "flex-1" },
      { key: "quantity", label: "Aantal", type: "number", step: "0.01", min: 0, width: "w-20" },
      { key: "unit_price", label: "Waarde per stuk", type: "number", step: "0.01", min: 0, width: "w-24" },
      { key: "hs_code", label: "HS-code", type: "text", width: "w-28", placeholder: "Optioneel" },
      { key: "country_of_origin", label: "Oorsprong", type: "text", width: "w-28", placeholder: "Optioneel" },
      { key: "weight_kg", label: "Gewicht kg", type: "number", step: "0.01", min: 0, width: "w-24" },
    ],
    emptyLine: {
      description: "", quantity: 1, unit_price: 0,
      hs_code: "", country_of_origin: "", weight_kg: null,
    },
    extras: [
      { key: "vat_percent", label: "BTW %", type: "number", step: "0.01", min: 0, max: 100 },
    ],
    helpText: "Bewerk factuur/waardeverklaring. HS-code en oorsprong zijn optioneel — vul in voor douane-doeleinden.",
  },
};


function FieldRender({ field, value, onChange, disabled }) {
  if (field.textarea) {
    return (
      <Textarea
        data-testid={`edit-field-${field.nestedKey || field.key}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled}
        rows={2}
        className="rounded-none mt-1"
      />
    );
  }
  return (
    <Input
      data-testid={`edit-field-${field.nestedKey || field.key}`}
      type={field.type || "text"}
      step={field.step}
      min={field.min}
      max={field.max}
      value={value ?? ""}
      onChange={(e) => {
        const v = field.type === "number"
          ? (e.target.value === "" ? null : parseFloat(e.target.value))
          : e.target.value;
        onChange(v);
      }}
      placeholder={field.placeholder}
      disabled={disabled}
      className="rounded-none mt-1"
    />
  );
}


function LineRow({ idx, line, lineFields, onChange, onRemove, disabled }) {
  return (
    <div className="flex gap-2 items-end border-b border-gray-100 py-2" data-testid={`edit-line-${idx}`}>
      <div className="text-xs text-gray-500 w-8 pb-2">{idx + 1}.</div>
      {lineFields.map((f) => (
        <div key={f.key} className={f.width || "flex-1"}>
          <Label className="text-[10px] uppercase tracking-wide text-gray-500">{f.label}</Label>
          <FieldRender
            field={{ ...f, nestedKey: `line-${idx}-${f.key}` }}
            value={line[f.key]}
            onChange={(v) => onChange({ ...line, [f.key]: v })}
            disabled={disabled}
          />
        </div>
      ))}
      <button
        onClick={onRemove}
        disabled={disabled}
        className="p-1.5 text-red-500 hover:text-red-700 mb-1"
        title="Regel verwijderen"
        data-testid={`edit-line-${idx}-remove`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}


export default function DocumentEditDialog({ open, onOpenChange, docType, doc, onSaved }) {
  const config = documentConfig[docType];
  const { hasPermission, user } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const canManage = docType === "generated_invoice" ? hasPermission("invoices:edit") :
    hasPermission("orders:convert_financial") ||
    ["owner", "admin", "manager"].includes(userRole);
  const [form, setForm] = useState({});
  const [lines, setLines] = useState([]);
  const [items, setItems] = useState([]);  // Sprint 2 — booking-form items
  const [wantsValueInvoice, setWantsValueInvoice] = useState(false);
  const [currency, setCurrency] = useState("EUR");
  const [editReason, setEditReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("verzender");

  const isBookingForm = docType === "booking_form";

  useEffect(() => {
    if (open && doc && config) {
      const initial = { ...doc };
      setForm(initial);
      setLines(config.linesKey ? [...(doc[config.linesKey] || [])] : []);
      setItems(isBookingForm ? [...(doc.items || [])] : []);
      setWantsValueInvoice(!!doc.wants_value_invoice);
      setCurrency(doc.value_invoice_currency || "EUR");
      setEditReason("");
      setActiveTab(config.sections[0].id);
    }
  }, [open, doc, config, isBookingForm]);

  if (!config) return null;

  const updateField = (sectionId, key, value) => {
    const section = config.sections.find((s) => s.id === sectionId);
    if (section?.nested) {
      setForm((prev) => ({
        ...prev,
        [section.nested]: { ...(prev[section.nested] || {}), [key]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  const getFieldValue = (sectionId, key) => {
    const section = config.sections.find((s) => s.id === sectionId);
    if (section?.nested) {
      return (form[section.nested] || {})[key];
    }
    return form[key];
  };

  const addLine = () => setLines((prev) => [...prev, { ...config.emptyLine }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));
  const updateLine = (idx, next) => setLines((prev) => prev.map((l, i) => (i === idx ? next : l)));

  const onSave = async () => {
    setSaving(true);
    try {
      // Build payload: alleen velden meesturen die zijn veranderd of altijd verplicht.
      // Voor eenvoud sturen we de bewerkte 'form' velden + lines + edit_reason.
      const payload = {};
      // Top-level scalars (geen nested, geen doc-id velden)
      config.sections.forEach((section) => {
        if (section.nested) {
          const nestedObj = form[section.nested] || {};
          if (Object.keys(nestedObj).length) {
            payload[section.nested] = section.fields.reduce((acc, f) => {
              if (nestedObj[f.key] !== undefined) acc[f.key] = nestedObj[f.key];
              return acc;
            }, {});
          }
        } else {
          section.fields.forEach((f) => {
            if (form[f.key] !== undefined && form[f.key] !== doc[f.key]) {
              payload[f.key] = form[f.key];
            }
          });
        }
      });
      // Extras (vat_percent etc.)
      (config.extras || []).forEach((f) => {
        if (form[f.key] !== undefined && form[f.key] !== doc[f.key]) {
          payload[f.key] = form[f.key];
        }
      });
      // Line items — pre-valideer dat geen regel een lege verplichte
      // omschrijving heeft (anders krijgen we backend 422 zonder duidelijke
      // melding voor de gebruiker).
      if (config.linesKey) {
        const emptyLineIdx = lines.findIndex(
          (l) => !l.description || String(l.description).trim() === ""
        );
        if (emptyLineIdx !== -1) {
          toast.error(
            `Regel ${emptyLineIdx + 1} heeft geen omschrijving. Vul deze in of verwijder de regel voordat je opslaat.`
          );
          setActiveTab("regels");
          setSaving(false);
          return;
        }
        payload[config.linesKey] = lines;
      }
      // Sprint 2 — Booking-form items + waardefactuur toggle
      if (isBookingForm) {
        // Blokkeer opslaan wanneer een itemregel niet volledig is ingevuld
        const errors = validateBookingItems(items, wantsValueInvoice);
        if (errors.length) {
          const first = errors[0];
          toast.error(
            `Item ${first.index + 1}: vul deze itemregel volledig in of verwijder de regel. Ontbrekend: ${first.missing.join(", ")}`,
          );
          setActiveTab("items");
          setSaving(false);
          return;
        }
        // Alleen meesturen wanneer items of toggles daadwerkelijk gewijzigd zijn
        const origItems = doc.items || [];
        if (JSON.stringify(items) !== JSON.stringify(origItems)) {
          payload.items = items;
        }
        if (!!doc.wants_value_invoice !== wantsValueInvoice) {
          payload.wants_value_invoice = wantsValueInvoice;
        }
        if ((doc.value_invoice_currency || "EUR") !== currency) {
          payload.value_invoice_currency = currency;
        }
      }

      if (editReason) payload.edit_reason = editReason;

      if (Object.keys(payload).length === 0) {
        toast.info("Geen wijzigingen om op te slaan.");
        setSaving(false);
        return;
      }

      const r = await adminApi.patch(config.apiPath(doc.id), payload);
      toast.success(`${config.label} bijgewerkt`);
      onSaved && onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onRegenerate = async () => {
    if (!config.regeneratePath) return;
    try {
      await adminApi.post(config.regeneratePath(doc.id));
      toast.success("PDF opnieuw gegenereerd");
      onSaved && onSaved({ ...doc, updated_at: new Date().toISOString() });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const linesExtras = config.extras || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl rounded-none max-h-[90vh] overflow-hidden flex flex-col" data-testid="document-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#0A192F]">
            {config.label} bewerken
          </DialogTitle>
          {(doc.invoice_number || doc.id) && (
            <div className="text-xs text-gray-500 font-mono">
              Documentnummer: <strong>{doc.invoice_number || (doc.id || "").slice(0, 8)}</strong> · blijft ongewijzigd
              {doc.revision != null && (
                <> · revisie #{(doc.revision || 0) + 1}</>
              )}
            </div>
          )}
        </DialogHeader>

        <div className="bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-sm" data-testid="edit-help">
          <AlertTriangle className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-gray-700">{config.helpText}</div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-3">
            <TabsList className="bg-gray-100 rounded-none">
              {config.sections.map((section) => (
                <TabsTrigger
                  key={section.id}
                  value={section.id}
                  className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white text-xs"
                  data-testid={`edit-tab-${section.id}`}
                >
                  {section.title}
                </TabsTrigger>
              ))}
              {config.linesKey && (
                <TabsTrigger
                  value="regels"
                  className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white text-xs"
                  data-testid="edit-tab-regels"
                >
                  Regels ({lines.length})
                </TabsTrigger>
              )}
              {isBookingForm && (
                <TabsTrigger
                  value="items"
                  className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white text-xs"
                  data-testid="edit-tab-items"
                >
                  Items ({items.length})
                </TabsTrigger>
              )}
            </TabsList>

            {config.sections.map((section) => (
              <TabsContent key={section.id} value={section.id} className="mt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-white p-3 border border-gray-100">
                  {section.fields.map((field) => (
                    <div key={field.key} className={field.textarea ? "md:col-span-2" : ""}>
                      <Label className="text-xs uppercase tracking-wide font-bold">{field.label}</Label>
                      <FieldRender
                        field={field}
                        value={getFieldValue(section.id, field.key)}
                        onChange={(v) => updateField(section.id, field.key, v)}
                        disabled={saving}
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}

            {config.linesKey && (
              <TabsContent value="regels" className="mt-3" data-testid="edit-lines-content">
                {linesExtras.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-white p-3 border border-gray-100 mb-2">
                    {linesExtras.map((f) => (
                      <div key={f.key}>
                        <Label className="text-xs uppercase tracking-wide font-bold">{f.label}</Label>
                        <FieldRender
                          field={f}
                          value={form[f.key]}
                          onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))}
                          disabled={saving}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <div className="bg-white p-3 border border-gray-100">
                  {lines.length === 0 && (
                    <div className="text-sm text-gray-500 py-8 text-center">Geen regels. Klik op &quot;Regel toevoegen&quot;.</div>
                  )}
                  {lines.map((ln, idx) => (
                    <LineRow
                      key={idx}
                      idx={idx}
                      line={ln}
                      lineFields={config.lineFields}
                      onChange={(next) => updateLine(idx, next)}
                      onRemove={() => removeLine(idx)}
                      disabled={saving}
                    />
                  ))}
                  <Button
                    onClick={addLine}
                    variant="outline"
                    className="rounded-none mt-3 text-xs"
                    disabled={saving}
                    data-testid="edit-line-add"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Regel toevoegen
                  </Button>
                </div>
              </TabsContent>
            )}

            {isBookingForm && (
              <TabsContent value="items" className="mt-3" data-testid="edit-items-content">
                {/* Waardefactuur toggle + valuta */}
                <div className="bg-white p-3 border border-gray-100 mb-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2 flex-1">
                    <Switch
                      checked={wantsValueInvoice}
                      onCheckedChange={setWantsValueInvoice}
                      disabled={saving || !canManage}
                      data-testid="booking-wants-value-invoice-toggle"
                    />
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-bold">
                        Ook waardefactuur genereren?
                      </Label>
                      <div className="text-[11px] text-gray-500">
                        Aan → per item is waarde/stuk verplicht en kan een waardeverklaring worden gegenereerd.
                      </div>
                    </div>
                  </div>
                  {wantsValueInvoice && (
                    <div className="flex-shrink-0">
                      <Label className="text-[10px] uppercase tracking-wide font-bold text-gray-600">
                        Valuta
                      </Label>
                      <Select value={currency} onValueChange={setCurrency}
                              disabled={saving || !canManage}>
                        <SelectTrigger className="rounded-none mt-1 text-sm h-9 w-32"
                                       data-testid="booking-currency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SUPPORTED_CURRENCIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <BookingItemsEditor
                  items={items}
                  onChange={setItems}
                  currency={currency}
                  wantsValueInvoice={wantsValueInvoice}
                  disabled={saving || !canManage}
                />
              </TabsContent>
            )}
          </Tabs>
        </div>

        <div className="mt-3 border-t border-gray-200 pt-3">
          <Label className="text-xs uppercase tracking-wide font-bold">Reden van wijziging (optioneel)</Label>
          <Input
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            placeholder="Bv. klantverzoek, bedrag gecorrigeerd, adres gecorrigeerd"
            disabled={saving}
            className="rounded-none mt-1"
            data-testid="edit-reason"
          />
        </div>

        <DialogFooter className="gap-2 mt-3">
          {config.regeneratePath && (
            <Button
              onClick={onRegenerate}
              variant="outline"
              className="rounded-none mr-auto"
              disabled={saving}
              data-testid="edit-regenerate-pdf"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              PDF opnieuw genereren
            </Button>
          )}
          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="rounded-none"
            disabled={saving}
            data-testid="edit-cancel"
          >
            Annuleren
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="rounded-none bg-[#0A192F] hover:bg-[#0055FF] text-white font-chivo font-bold uppercase"
            data-testid="edit-save"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
